import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Input, Badge, Table } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import { fetchProducts, setProductExcluded } from '../../actions/cspRenewalsActions'

const formatCurrency = (n) =>
  n == null ? '–' : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })

const formatRange = (min, max) => {
  if (min == null && max == null) return '–'
  if (min == null) return formatCurrency(max)
  if (max == null) return formatCurrency(min)
  if (min === max) return formatCurrency(min)
  return `${formatCurrency(min)} – ${formatCurrency(max)}`
}

// Sort by max RRP desc by default - admin curating wants the most-expensive
// products at the top so they can clear the long tail of free SKUs without
// scrolling through paid ones.
const SORT_OPTIONS = [
  { key: 'maxRrpDesc', label: 'Highest RRP', cmp: (a, b) => (b.maxRrp ?? -1) - (a.maxRrp ?? -1) },
  { key: 'maxRrpAsc', label: 'Lowest RRP', cmp: (a, b) => (a.maxRrp ?? Number.MAX_SAFE_INTEGER) - (b.maxRrp ?? Number.MAX_SAFE_INTEGER) },
  { key: 'descAsc', label: 'Name A→Z', cmp: (a, b) => (a.displayName ?? a.stockDescription ?? '').localeCompare(b.displayName ?? b.stockDescription ?? '') },
  { key: 'subsDesc', label: 'Most active subs', cmp: (a, b) => (b.activeSubscriptionCount ?? 0) - (a.activeSubscriptionCount ?? 0) },
]

const CSPProductsInner = ({ isAdmin }) => {
  const dispatch = useDispatch()
  const products = useSelector((s) => s.cspRenewals.products) || []
  const loading = useSelector((s) => s.cspRenewals.productsLoading)
  const updating = useSelector((s) => s.cspRenewals.updatingStockCode)

  const [search, setSearch] = useState('')
  const [view, setView] = useState('all') // 'all' | 'included' | 'excluded'
  const [sortKey, setSortKey] = useState('maxRrpDesc')

  useEffect(() => {
    dispatch(fetchProducts())
  }, [dispatch])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = products
    if (view === 'included') rows = rows.filter((p) => !p.isExcluded)
    else if (view === 'excluded') rows = rows.filter((p) => p.isExcluded)
    if (q) {
      rows = rows.filter((p) =>
        [p.stockCode, p.displayName, p.stockDescription]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(q))
      )
    }
    const cmp = (SORT_OPTIONS.find((o) => o.key === sortKey) ?? SORT_OPTIONS[0]).cmp
    return [...rows].sort(cmp)
  }, [products, search, view, sortKey])

  const totalCount = products.length
  const includedCount = products.filter((p) => !p.isExcluded).length
  const excludedCount = products.filter((p) => p.isExcluded).length

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-3">
          <Col>
            <Link to="/CSPRenewals" className="small text-muted">&larr; Back to CSP Renewals</Link>
            <PageHeading>CSP Products</PageHeading>
          </Col>
        </Row>

        <p className="text-muted small">
          Curate which CSP product types appear on the renewals dashboard and trigger
          notifications. Toggle a product off to hide its subscriptions from the dashboard
          and skip it during notification dispatch — useful for $0 SKUs (free trials,
          internal-use copies) that would otherwise pollute renewal emails.
        </p>

        <Row className="mb-3 align-items-center">
          <Col md={5} className="mb-2 mb-md-0">
            <Input
              type="search"
              placeholder="Search by stock code or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bsSize="sm"
            />
          </Col>
          <Col md={4}>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${view === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setView('all')}
              >
                All <Badge color={view === 'all' ? 'light' : 'primary'} className={`ml-1 ${view === 'all' ? 'text-dark' : ''}`}>{totalCount}</Badge>
              </button>
              <button
                type="button"
                className={`btn ${view === 'included' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setView('included')}
              >
                Notify <Badge color={view === 'included' ? 'light' : 'primary'} className={`ml-1 ${view === 'included' ? 'text-dark' : ''}`}>{includedCount}</Badge>
              </button>
              <button
                type="button"
                className={`btn ${view === 'excluded' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setView('excluded')}
              >
                Excluded <Badge color={view === 'excluded' ? 'light' : 'primary'} className={`ml-1 ${view === 'excluded' ? 'text-dark' : ''}`}>{excludedCount}</Badge>
              </button>
            </div>
          </Col>
          <Col md={3}>
            <Input
              type="select"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              bsSize="sm"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>Sort: {o.label}</option>
              ))}
            </Input>
          </Col>
        </Row>

        {loading && products.length === 0 ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <p className="text-muted">
            {search ? `No products match "${search}".` : 'No products synced yet.'}
          </p>
        ) : (
          <Table hover bordered size="sm" className="bg-white">
            <thead>
              <tr>
                <th style={{ width: '90px' }}>Notify</th>
                <th>Stock code</th>
                <th>Product</th>
                <th style={{ width: '90px', textAlign: 'right' }}>Active subs</th>
                <th style={{ width: '180px', textAlign: 'right' }}>Unit price</th>
                <th style={{ width: '180px', textAlign: 'right' }}>RRP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const isAuto = p.isExcluded && !p.isManuallyOverridden
                const isUpdating = updating === p.stockCode
                return (
                  <tr key={p.stockCode} style={{ opacity: p.isExcluded ? 0.65 : 1 }}>
                    <td>
                      <div className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          className="m-0 mr-2"
                          style={{ position: 'relative', marginTop: 0, transform: 'scale(1.1)' }}
                          checked={!p.isExcluded}
                          disabled={!isAdmin || isUpdating}
                          onChange={(e) => dispatch(setProductExcluded(p.stockCode, !e.target.checked))}
                        />
                        {isAuto && (
                          <Badge color="secondary" pill title="Auto-excluded by sync (both UnitPrice and RRP are 0). Toggle to take admin ownership.">
                            Auto
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <code className="small">{p.stockCode}</code>
                    </td>
                    <td>
                      {(() => {
                        const friendly = p.displayName
                        const technical = p.stockDescription
                        // Dedup when DD's friendly + technical fields collapse
                        // to the same string (e.g. SubscriptionReference was
                        // missing on every observed sub).
                        if (friendly && technical && friendly !== technical) {
                          return (
                            <>
                              <div className="font-weight-bold">{friendly}</div>
                              <div className="small text-muted">{technical}</div>
                            </>
                          )
                        }
                        const single = friendly || technical
                        return single
                          ? <div className="font-weight-bold">{single}</div>
                          : <span className="text-muted">–</span>
                      })()}
                    </td>
                    <td style={{ textAlign: 'right' }}>{p.activeSubscriptionCount ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{formatRange(p.minUnitPrice, p.maxUnitPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{formatRange(p.minRrp, p.maxRrp)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Container>
    </PageContainer>
  )
}

const CSPProducts = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const roles = accountInfo?.account?.idToken?.roles || []
      const isAdmin = roles.includes('Admin')
      const isSales = roles.includes('Sales')

      if (!isAdmin && !isSales) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }

      // Sales has full edit rights inside the CSP renewals dashboard.
      return <CSPProductsInner isAdmin={isAdmin || isSales} />
    }}
  </AzureAD>
)

export default CSPProducts
